import React, { useState, useEffect } from 'react';
import { Megaphone, Activity, CheckCircle, PieChart, TrendingUp, Compass, MessageSquare, Briefcase, LayoutTemplate, FileSearch, Kanban, RefreshCw, Layers, Globe, Linkedin, Facebook, Instagram, Youtube, Link, Sparkles, Share2, Send, Cpu } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const seoData = [
  { day: 'MON', score: 82 },
  { day: 'TUE', score: 85 },
  { day: 'WED', score: 84 },
  { day: 'THU', score: 89 },
  { day: 'FRI', score: 92 },
  { day: 'SAT', score: 95 },
  { day: 'SUN', score: 98 },
];

export const AssetForgeWorkspace: React.FC = () => {
  const [themeMode, setThemeMode] = useState<'NEXORAX_GOLD' | 'TACTICAL_JADE' | 'MIDNIGHT_OBSIDIAN'>('TACTICAL_JADE');
  const [gridVisible, setGridVisible] = useState(true);
  const [laserPulse, setLaserPulse] = useState(true);
  const [nodeSize, setNodeSize] = useState(6);
  const [glowIntensity, setGlowIntensity] = useState(25);
  const [logoRotation, setLogoRotation] = useState(0);
  const [gridOpacity, setGridOpacity] = useState(0.4);

  // Core color mapping based on selected theme mode
  const themeColors = {
    NEXORAX_GOLD: {
      primary: '#d97706', // Amber-600
      secondary: '#fbbf24', // Amber-400
      accent: '#22d3ee', // Cyan-400
      glow: 'rgba(217, 119, 6, 0.45)',
      cardBg: 'from-amber-950/20 to-slate-950/90',
    },
    TACTICAL_JADE: {
      primary: '#059669', // Emerald-600
      secondary: '#34d399', // Emerald-400
      accent: '#a78bfa', // Violet-400
      glow: 'rgba(5, 150, 105, 0.45)',
      cardBg: 'from-emerald-950/20 to-slate-950/90',
    },
    MIDNIGHT_OBSIDIAN: {
      primary: '#2563eb', // Blue-600
      secondary: '#60a5fa', // Blue-400
      accent: '#f43f5e', // Rose-400
      glow: 'rgba(37, 99, 235, 0.45)',
      cardBg: 'from-blue-950/20 to-slate-950/90',
    }
  }[themeMode];

  const handleDownloadSVG = () => {
    const svgElement = document.getElementById('nexorax_forge_svg');
    if (!svgElement) return;
    const svgString = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const downloadAnchor = document.createElement('a');
    downloadAnchor.href = url;
    downloadAnchor.download = `nexorax_NexoraX_identity_${themeMode.toLowerCase()}.svg`;
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    document.body.removeChild(downloadAnchor);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 flex flex-col lg:flex-row gap-6 bg-[#02050c]/30 rounded-xl p-4 border border-slate-800/80">
      {/* Visual Canvas Panel */}
      <div className="flex-1 bg-black/60 border border-slate-800/60 rounded-xl p-4 flex flex-col relative overflow-hidden select-none min-h-[380px]">
        {/* Subtle decorative target scopes */}
        <div className="absolute top-3 left-3 w-4 h-4 border-t border-l border-slate-700/60"></div>
        <div className="absolute top-3 right-3 w-4 h-4 border-t border-r border-slate-700/60"></div>
        <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l border-slate-700/60"></div>
        <div className="absolute bottom-3 right-3 w-4 h-4 border-b border-r border-slate-700/60"></div>

        {/* Info label */}
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-1">
          <span className="font-mono text-[9px] text-[#22d3ee] tracking-widest uppercase font-extrabold flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
            NEXORAX NEXORAX VECTOR NODE
          </span>
          <span className="font-mono text-[8px] text-slate-500 uppercase tracking-wider">
            LICENSED CORE • PAT-PEND ID: SC887095-B9
          </span>
        </div>

        {/* Error mitigation / True representation notice overlay */}
        <div className="absolute bottom-4 left-4 z-10 max-w-sm">
          <p className="font-mono text-[8px] text-slate-500 uppercase leading-normal">
            * Note: Server-side Imagen generation quota exceeded. Dynamic local hardware-vector engine active default. Rendering at lossless resolution.
          </p>
        </div>

        {/* Live Vector SVG Area */}
        <div className="flex-grow flex items-center justify-center p-6 relative">
          <svg
            id="nexorax_forge_svg"
            viewBox="0 0 500 300"
            width="100%"
            height="100%"
            className="max-h-[300px] w-full"
            style={{ filter: `drop-shadow(0 0 ${glowIntensity}px ${themeColors.glow})` }}
          >
            <defs>
              <linearGradient id="gradPrimary" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={themeColors.primary} />
                <stop offset="100%" stopColor={themeColors.secondary} />
              </linearGradient>
              <linearGradient id="gradAccent" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={themeColors.accent} stopOpacity="0.8" />
                <stop offset="100%" stopColor={themeColors.primary} stopOpacity="0.1" />
              </linearGradient>
              <pattern id="dotGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1.2" fill="#1e293b" fillOpacity="0.55" />
              </pattern>
            </defs>

            {/* Background Dot Layer */}
            {gridVisible && (
              <rect width="500" height="300" fill="url(#dotGrid)" opacity={gridOpacity} />
            )}

            {/* Grid Crosshair Coordinates */}
            {gridVisible && (
              <g stroke="#1e293b" strokeWidth="0.5" strokeOpacity={gridOpacity}>
                <line x1="250" y1="0" x2="250" y2="300" strokeDasharray="4 4" />
                <line x1="0" y1="150" x2="500" y2="150" strokeDasharray="4 4" />
              </g>
            )}

            {/* Moving Laser Sweep Line */}
            {laserPulse && (
              <line
                x1="0"
                y1="0"
                x2="500"
                y2="0"
                stroke={themeColors.accent}
                strokeWidth="1.5"
                strokeOpacity="0.4"
                className="animated-sweep"
                style={{
                  animation: 'sweep 3.5s ease-in-out infinite',
                }}
              />
            )}

            {/* The Central NexoraX Geometric Crest Symbol */}
            <g transform={`translate(250, 150) rotate(${logoRotation})`}>
              {/* Surrounding Ring Segment */}
              <circle
                cx="0"
                cy="0"
                r="72"
                fill="none"
                stroke={themeColors.primary}
                strokeWidth="1.5"
                strokeOpacity="0.3"
                strokeDasharray="5 15"
              />
              <circle
                cx="0"
                cy="0"
                r="64"
                fill="none"
                stroke="url(#gradAccent)"
                strokeWidth="0.75"
                strokeOpacity="0.5"
              />

              {/* Central Sleek 'N' Vector Pathways */}
              {/* Backing Shadow Line */}
              <path
                d="M -40 40 L -40 -40 L 40 40 L 40 -40"
                fill="none"
                stroke="rgba(0,0,0,0.5)"
                strokeWidth="11"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Main Core Ribbon */}
              <path
                d="M -40 40 L -40 -40 L 40 40 L 40 -40"
                fill="none"
                stroke="url(#gradPrimary)"
                strokeWidth="8.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Core Accent Energy Channel */}
              <path
                d="M -40 40 L -40 -40 L 40 40 L 40 -40"
                fill="none"
                stroke="#ffffff"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeOpacity="0.95"
              />

              {/* Dynamic Cyber Nodes */}
              <circle cx="-40" cy="40" r={nodeSize} fill={themeColors.accent} stroke="#ffffff" strokeWidth="1.5" />
              <circle cx="-40" cy="-40" r={nodeSize} fill={themeColors.secondary} stroke="#ffffff" strokeWidth="1.5" />
              <circle cx="40" cy="40" r={nodeSize} fill={themeColors.secondary} stroke="#ffffff" strokeWidth="1.5" />
              <circle cx="40" cy="-40" r={nodeSize} fill={themeColors.accent} stroke="#ffffff" strokeWidth="1.5" />

              {/* Center Orbit Pulse */}
              <circle cx="0" cy="0" r="14" fill="#02050c" stroke={themeColors.primary} strokeWidth="2.5" />
              <circle cx="0" cy="0" r="4" fill={themeColors.accent} />
            </g>

            {/* Typography elements printed directly inside vector */}
            <text x="250" y="260" textAnchor="middle" fill="#ffffff" fontFamily="Inter, sans-serif" fontWeight="800" fontSize="16" letterSpacing="6" className="uppercase">
              NEXORAX
            </text>
            <text x="250" y="278" textAnchor="middle" fill={themeColors.primary} fontFamily="JetBrains Mono, monospace" fontWeight="600" fontSize="7.5" letterSpacing="4" className="uppercase">
              NexoraX Operating System
            </text>
          </svg>
        </div>
      </div>

      {/* Control Panel Panel */}
      <div className="w-full lg:w-80 bg-[#070b16]/90 border border-slate-800/80 p-5 rounded-xl flex flex-col gap-5 shrink-0 select-none">
        
        {/* Core Theme Options */}
        <div className="space-y-3">
          <span className="font-mono text-[9px] text-slate-400 font-bold uppercase tracking-widest block">
            1. CHOOSE BRAND PRESENCE
          </span>
          <div className="grid grid-cols-1 gap-1.5">
            <button
              onClick={() => setThemeMode('NEXORAX_GOLD')}
              className={`px-3 py-2 rounded text-left border flex items-center justify-between transition-all ${
                themeMode === 'NEXORAX_GOLD'
                  ? 'bg-amber-500/10 border-amber-500 text-amber-300 shadow-[0_0_12px_rgba(245,158,11,0.2)]'
                  : 'bg-black/40 border-slate-800 hover:border-slate-700 text-slate-400'
              }`}
            >
              <span className="font-mono text-[10px] uppercase font-bold tracking-wider">NexoraX Gold Accent</span>
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
            </button>
            <button
              onClick={() => setThemeMode('TACTICAL_JADE')}
              className={`px-3 py-2 rounded text-left border flex items-center justify-between transition-all ${
                themeMode === 'TACTICAL_JADE'
                  ? 'bg-emerald-500/10 border-emerald-500 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.2)]'
                  : 'bg-black/40 border-slate-800 hover:border-slate-700 text-slate-400'
              }`}
            >
              <span className="font-mono text-[10px] uppercase font-bold tracking-wider">Tactical Jade Accent</span>
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
            </button>
            <button
              onClick={() => setThemeMode('MIDNIGHT_OBSIDIAN')}
              className={`px-3 py-2 rounded text-left border flex items-center justify-between transition-all ${
                themeMode === 'MIDNIGHT_OBSIDIAN'
                  ? 'bg-blue-500/10 border-blue-500 text-blue-300 shadow-[0_0_12px_rgba(59,130,246,0.2)]'
                  : 'bg-black/40 border-slate-800 hover:border-slate-700 text-slate-400'
              }`}
            >
              <span className="font-mono text-[10px] uppercase font-bold tracking-wider">Midnight Obsidian</span>
              <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
            </button>
          </div>
        </div>

        {/* CSS Slider Nodes */}
        <div className="space-y-4 pt-1">
          <span className="font-mono text-[9px] text-slate-400 font-bold uppercase tracking-widest block">
            2. VECTOR PARAMETERS
          </span>

          {/* Glow Slider */}
          <div className="space-y-1.5">
            <div className="flex justify-between font-mono text-[8px] uppercase tracking-wider text-slate-400">
              <span>Glow Aura</span>
              <span className="text-cyan-400">{glowIntensity}px</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              value={glowIntensity}
              onChange={(e) => setGlowIntensity(Number(e.target.value))}
              className="w-full accent-cyan-400 bg-slate-900 cursor-pointer h-1.5 rounded-lg"
            />
          </div>

          {/* Node Size Slider */}
          <div className="space-y-1.5">
            <div className="flex justify-between font-mono text-[8px] uppercase tracking-wider text-slate-400">
              <span>Dynamic Node Size</span>
              <span className="text-cyan-400">{nodeSize}pt</span>
            </div>
            <input
              type="range"
              min="3"
              max="9"
              step="0.5"
              value={nodeSize}
              onChange={(e) => setNodeSize(Number(e.target.value))}
              className="w-full accent-cyan-400 bg-slate-900 cursor-pointer h-1.5 rounded-lg"
            />
          </div>

          {/* Logo Rotation Slider */}
          <div className="space-y-1.5">
            <div className="flex justify-between font-mono text-[8px] uppercase tracking-wider text-slate-400">
              <span>Path Orientation</span>
              <span className="text-cyan-400">{logoRotation}°</span>
            </div>
            <input
              type="range"
              min="0"
              max="360"
              value={logoRotation}
              onChange={(e) => setLogoRotation(Number(e.target.value))}
              className="w-full accent-cyan-400 bg-slate-900 cursor-pointer h-1.5 rounded-lg"
            />
          </div>

          {/* Grid Opacity Slider */}
          <div className="space-y-1.5">
            <div className="flex justify-between font-mono text-[8px] uppercase tracking-wider text-slate-400">
              <span>Grid Opacity</span>
              <span className="text-cyan-400">{Math.round(gridOpacity * 100)}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={gridOpacity}
              onChange={(e) => setGridOpacity(Number(e.target.value))}
              className="w-full accent-cyan-400 bg-slate-900 cursor-pointer h-1.5 rounded-lg"
            />
          </div>
        </div>

        {/* Structural Toggles */}
        <div className="space-y-2 pt-1 border-t border-slate-850">
          <div className="flex items-center justify-between bg-black/30 p-2 rounded border border-slate-900">
            <span className="font-mono text-[9px] text-slate-400 uppercase tracking-widest">Render Alignment Grid</span>
            <input
              type="checkbox"
              checked={gridVisible}
              onChange={() => setGridVisible(!gridVisible)}
              className="rounded accent-cyan-400 bg-slate-800"
            />
          </div>
          <div className="flex items-center justify-between bg-black/30 p-2 rounded border border-slate-900">
            <span className="font-mono text-[9px] text-slate-400 uppercase tracking-widest">Laser Sweep Wave</span>
            <input
              type="checkbox"
              checked={laserPulse}
              onChange={() => setLaserPulse(!laserPulse)}
              className="rounded accent-cyan-400 bg-slate-800"
            />
          </div>
        </div>

        {/* Download Action */}
        <button
          onClick={handleDownloadSVG}
          className="w-full mt-auto py-2.5 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white font-mono text-[9px] tracking-widest uppercase font-bold rounded shadow-lg transition-all"
        >
          DOWNLOAD CRYPTOGRAPHIC SVG
        </button>

        {/* Legal Protections Panel */}
        <div className="p-3 bg-black/60 rounded border border-slate-800 text-[8px] font-mono text-slate-500 uppercase tracking-wide leading-relaxed">
          <strong className="text-slate-400 block mb-1">IP NEXORAX CLAIM</strong>
          NexoraX Digital Ltd (SC887095) • Global patents pending in Dunfermline NexoraX Staging Terminal. All vector architectures cryptographically sealed. CloudSync-02 active monitoring.
        </div>

      </div>

      <style>{`
        @keyframes sweep {
          0% { transform: translateY(0); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(300px); opacity: 0; }
        }
      `}</style>
    </div>
  );
};



export const BrandingTerminal: React.FC = () => {
  const [activeStrategy, setActiveStrategy] = useState<string>(() => {
    return localStorage.getItem('nexorax_branding_active_strategy') || 'DIRECTOR_BRANDING';
  });
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastLog, setToastLog] = useState<{ id: number, message: string, timestamp: Date, status: string }[]>(() => {
    const saved = localStorage.getItem('nexorax_branding_toast_log');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.map((item: any) => ({
          ...item,
          timestamp: item.timestamp ? new Date(item.timestamp) : new Date()
        }));
      } catch (e) {
        console.error(e);
      }
    }
    return [];
  });
  const [showToastLog, setShowToastLog] = useState(false);
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('ALL');
  const [isGenerating, setIsGenerating] = useState(false);
  const [assetsGenerated, setAssetsGenerated] = useState(false);
  
  // Director Personal Social Media Node States
  const [personalPlatform, setPersonalPlatform] = useState<string>(() => {
    return localStorage.getItem('nexorax_branding_personal_platform') || 'LinkedIn';
  });
  const [personalTheme, setPersonalTheme] = useState<string>(() => {
    return localStorage.getItem('nexorax_branding_personal_theme') || '£150,000 Resource Funnel Pitch';
  });
  const [personalDirectives, setPersonalDirectives] = useState<string>(() => {
    return localStorage.getItem('nexorax_branding_personal_directives') || '';
  });
  const [personalAudience, setPersonalAudience] = useState<string>(() => {
    return localStorage.getItem('nexorax_branding_personal_audience') || 'Enterprise Partners';
  });
  const [generatedNexoraXPost, setGeneratedNexoraXPost] = useState<string>(() => {
    return localStorage.getItem('nexorax_branding_generated_NexoraX_post') || '';
  });
  const [isGeneratingNexoraXPost, setIsGeneratingNexoraXPost] = useState<boolean>(false);

  useEffect(() => {
    localStorage.setItem('nexorax_branding_active_strategy', activeStrategy);
  }, [activeStrategy]);

  useEffect(() => {
    localStorage.setItem('nexorax_branding_toast_log', JSON.stringify(toastLog));
  }, [toastLog]);

  useEffect(() => {
    localStorage.setItem('nexorax_branding_personal_platform', personalPlatform);
  }, [personalPlatform]);

  useEffect(() => {
    localStorage.setItem('nexorax_branding_personal_theme', personalTheme);
  }, [personalTheme]);

  useEffect(() => {
    localStorage.setItem('nexorax_branding_personal_directives', personalDirectives);
  }, [personalDirectives]);

  useEffect(() => {
    localStorage.setItem('nexorax_branding_personal_audience', personalAudience);
  }, [personalAudience]);

  useEffect(() => {
    localStorage.setItem('nexorax_branding_generated_NexoraX_post', generatedNexoraXPost);
  }, [generatedNexoraXPost]);

  const showToast = (msg: string, status: 'SUCCESS' | 'INFO' | 'WARNING' | 'ERROR' = 'SUCCESS') => {
    setToastMessage(msg);
    setToastLog(prev => [{ id: Date.now(), message: msg, timestamp: new Date(), status }, ...prev]);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const filteredLog = toastLog.filter(log => {
    const matchesSearch = log.message.toLowerCase().includes(logSearchQuery.toLowerCase());
    const matchesStatus = logStatusFilter === 'ALL' || log.status === logStatusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleGenerateAssets = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setAssetsGenerated(true);
      showToast('BRAND ASSETS GENERATED & SECURED');
    }, 2000);
  };

  const handleGenerateNexoraXPost = async () => {
    setIsGeneratingNexoraXPost(true);
    setGeneratedNexoraXPost('');
    showToast('INITIATING NEXORAX BRAND RECOGNITION...');
    
    try {
      const response = await fetch('/api/admin/branding-social', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platform: personalPlatform,
          theme: personalTheme,
          additionalGuidelines: personalDirectives,
          audience: personalAudience
        })
      });
      
      const data = await response.json();
      if (data.status === 'success' || data.status === 'fallback') {
        setGeneratedNexoraXPost(data.content);
        showToast('NEXORAX CAMPAIGN COMPILED AND LOCKED', 'SUCCESS');
      } else {
        throw new Error(data.message || 'Unknown node synchronization response');
      }
    } catch (err: any) {
      console.error(err);
      showToast('TELEMETRY TIMEOUT - LOCAL PROTOCOL ACTIVATED', 'WARNING');
    } finally {
      setIsGeneratingNexoraXPost(false);
    }
  };

  const strategies = [
    { id: 'DIRECTOR_BRANDING', label: 'Director Social Pitch', icon: <Sparkles className="w-4 h-4 text-cyan-400" /> },
    { id: 'SEO_OPTIMIZATION', label: 'Website SEO Core', icon: <TrendingUp className="w-4 h-4" /> },
    { id: 'SEO_AUDIT', label: 'SEO Audit Report', icon: <FileSearch className="w-4 h-4" /> },
    { id: 'VISUAL_IDENTITY', label: 'Visual Identity Matrix', icon: <PieChart className="w-4 h-4" /> },
    { id: 'BRAND_VOICE', label: 'Brand Voice & Tone', icon: <Megaphone className="w-4 h-4" /> },
    { id: 'COMMUNITY_ENGAGEMENT', label: 'Community Engagement Loop', icon: <MessageSquare className="w-4 h-4" /> },
    { id: 'AUDIENCE_GROWTH', label: 'Audience Growth Strategies', icon: <Activity className="w-4 h-4" /> },
    { id: 'BRAND_GUIDELINES', label: 'Brand Guidelines Hub', icon: <Briefcase className="w-4 h-4" /> },
    { id: 'SOCIAL_TEMPLATES', label: 'Social Media Templates', icon: <LayoutTemplate className="w-4 h-4" /> },
    { id: 'PILLAR_BOARD', label: 'Pillar Strategy Board', icon: <Kanban className="w-4 h-4" /> },
    { id: 'SOCIAL_ACCOUNTS', label: 'NexoraX Social Directory', icon: <Globe className="w-4 h-4" /> },
    { id: 'ASSET_FORGE', label: 'Asset Forge', icon: <Layers className="w-4 h-4" /> },
  ];

  return (
    <div className="flex flex-col h-full space-y-6 max-w-6xl mx-auto w-full pb-10 mt-8 relative">
      {/* Toast Notification System */}
      <div className="fixed top-8 right-8 z-50 flex flex-col items-end gap-2 toast-notification-system">
        <div className={`transition-all duration-500 ease-out transform ${toastMessage ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
          <div className="bg-emerald-900/90 border border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.3)] backdrop-blur-md text-white px-6 py-3 rounded flex items-center justify-between gap-6 w-[320px]">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest">{toastMessage}</span>
            </div>
          </div>
        </div>

        {/* History Toggle Button */}
        {toastLog.length > 0 && (
          <button 
            onClick={() => setShowToastLog(!showToastLog)}
            className="text-[10px] bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 text-slate-400 hover:text-cyan-400 font-mono tracking-widest uppercase px-3 py-1.5 rounded transition-colors"
          >
            {showToastLog ? 'HIDE LOG' : `VIEW LOG (${toastLog.length})`}
          </button>
        )}

        {/* Scrollable History View */}
        {showToastLog && (
          <div className="w-[320px] max-h-[400px] bg-[#050914]/95 backdrop-blur-xl border border-slate-700/50 rounded-xl shadow-2xl flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5 bg-black/40 flex justify-between items-center">
              <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest font-bold">SYSTEM ACTION LOG</span>
              <button onClick={() => setShowToastLog(false)} className="text-slate-500 hover:text-white">&times;</button>
            </div>
            <div className="p-3 border-b border-slate-700/50 space-y-3 bg-black/60">
              <input 
                  type="text" 
                  placeholder="SEARCH ARCHIVES..." 
                  value={logSearchQuery}
                  onChange={e => setLogSearchQuery(e.target.value)}
                  className="w-full bg-[#0b0f19] border border-slate-700 px-3 py-2 text-[10px] text-white font-mono uppercase tracking-widest rounded outline-none focus:border-cyan-500 transition-colors"
              />
              <div className="flex justify-between items-center">
                <select 
                  value={logStatusFilter}
                  onChange={e => setLogStatusFilter(e.target.value)}
                  className="bg-[#0b0f19] border border-slate-700 text-[10px] text-slate-300 font-mono px-2 py-1.5 rounded outline-none focus:border-cyan-500 cursor-pointer uppercase tracking-widest"
                >
                  <option value="ALL">ALL STATUS</option>
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="INFO">INFO</option>
                  <option value="WARNING">WARNING</option>
                  <option value="ERROR">ERROR</option>
                </select>
                <button 
                  onClick={() => setToastLog([])}
                  className="text-[9px] text-rose-500 hover:text-rose-400 uppercase tracking-widest font-mono font-bold transition-colors"
                >
                  CLEAR LOG
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2 custom-scrollbar space-y-1 bg-[#050914]/80">
              {filteredLog.length === 0 ? (
                <div className="text-center p-6 text-slate-500 font-mono text-[10px] uppercase tracking-widest">
                  NO RECORDS FOUND
                </div>
              ) : (
                filteredLog.map(log => (
                  <div key={log.id} className="p-3 bg-slate-800/30 rounded border border-slate-700/30 flex flex-col gap-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-[9px] text-cyan-500/70 font-mono tracking-wider">
                        {log.timestamp.toLocaleTimeString()}
                      </span>
                      <span className={`text-[8px] font-mono px-1.5 py-0.5 rounded font-bold tracking-widest ${
                        log.status === 'SUCCESS' ? 'bg-emerald-900/40 text-emerald-400 border border-emerald-500/30' :
                        log.status === 'WARNING' ? 'bg-amber-900/40 text-amber-400 border border-amber-500/30' :
                        log.status === 'ERROR' ? 'bg-rose-900/40 text-rose-400 border border-rose-500/30' :
                        'bg-blue-900/40 text-blue-400 border border-blue-500/30'
                      }`}>
                        {log.status}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-300 font-mono tracking-wide uppercase leading-relaxed">
                      {log.message}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end border-b border-cyan-500/20 pb-6 mb-2 gap-4 shrink-0">
        <div>
          <span className="text-slate-500 font-mono text-[10px] uppercase tracking-widest block mb-2">INTEGRATION NODE 25: BRANDING SECURED</span>
          <h2 className="text-2xl font-bold text-white uppercase tracking-widest font-sans drop-shadow-md flex items-center gap-3">
            <Compass className="w-6 h-6 text-cyan-400 shrink-0" />
            BRANDING COMMAND MATRIX
          </h2>
        </div>
        
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={() => showToast('GLOBAL DIRECTIVES BROADCASTED')} className="flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600/80 to-blue-800/80 hover:from-cyan-500 hover:to-blue-700 border border-cyan-400/50 transition-all px-6 py-2.5 rounded shadow-[0_0_15px_rgba(34,211,238,0.3)] text-white group">
            <CheckCircle className="w-4 h-4 shadow-[0_0_10px_rgba(255,255,255,0.5)] rounded-full text-white" />
            <span className="font-mono text-[10px] uppercase tracking-[0.1em] font-bold">DEPLOY GLOBAL BRAND</span>
          </button>
        </div>
      </div>
      
      <div className="flex gap-6 h-full">
        <div className="w-64 bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-lg flex flex-col overflow-hidden shrink-0">
          <div className="h-12 border-b border-white/5 bg-black/40 flex items-center px-4">
             <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest font-bold">BRANDING MODULES</span>
          </div>
          <div className="p-2 flex-1 space-y-1">
             {strategies.map(s => (
               <button
                 key={s.id}
                 onClick={() => setActiveStrategy(s.id)}
                 className={`w-full flex items-center gap-3 px-4 py-3 rounded font-mono text-xs uppercase tracking-wider transition-all text-left ${activeStrategy === s.id ? 'bg-cyan-500/20 border border-cyan-500/50 text-cyan-300' : 'bg-transparent border border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white'}`}
               >
                 {s.icon}
                 <span className="truncate">{s.label}</span>
               </button>
             ))}
          </div>
        </div>

        <div className="flex-1 bg-[#050914]/80 backdrop-blur-md border border-slate-700/50 rounded-2xl shadow-lg overflow-hidden flex flex-col">
          <div className="h-12 border-b border-white/5 bg-black/40 flex items-center px-6">
            <h3 className="font-mono text-[11px] text-cyan-400 uppercase tracking-widest font-bold flex items-center gap-2">
              <Activity className="w-3 h-3" />
              ACTIVE MODULE: {strategies.find(s => s.id === activeStrategy)?.label}
            </h3>
          </div>
          <div className="p-6 flex-1 text-slate-300 overflow-y-auto custom-scrollbar text-left">
             {activeStrategy === 'DIRECTOR_BRANDING' && (
                <div className="space-y-6 animate-fade-in">
                   <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-cyan-500/20 pb-4">
                     <div>
                       <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Director NexoraX Social Media Node</h4>
                       <p className="text-sm font-sans leading-relaxed text-slate-400">Directly generate enterprise-grade social media pitches and announcements on behalf of Lead Director, Director.</p>
                     </div>
                     <span className="shrink-0 text-[9px] font-mono px-2.5 py-1 rounded bg-cyan-950/60 border border-cyan-500/30 text-cyan-400 font-extrabold tracking-widest uppercase">NODE-09.S COGNITIVE SHIELD</span>
                   </div>

                   <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                     {/* Parameters Form */}
                     <div className="space-y-4 bg-black/40 border border-slate-800 p-5 rounded-xl">
                       <span className="text-[10px] font-mono text-cyan-500 uppercase tracking-widest font-black block border-b border-slate-800 pb-2">CRITICAL BRAND ALIGNMENT PARAMETERS</span>
                       
                       <div className="space-y-1">
                         <label className="text-[9px] font-mono text-slate-400 uppercase tracking-wider block">Target Social Vector Platform</label>
                         <select 
                           value={personalPlatform} 
                           onChange={(e) => setPersonalPlatform(e.target.value)}
                           className="w-full bg-[#0b0f19] border border-slate-700 p-2.5 rounded text-white text-xs font-mono outline-none focus:border-cyan-500 cursor-pointer"
                         >
                           <option value="LinkedIn">LinkedIn Executive Feed</option>
                           <option value="X / Twitter">X / Twitter Route Gateway</option>
                           <option value="Facebook">Facebook Corporate Portal</option>
                           <option value="WhatsApp">WhatsApp Strategic Broadcast</option>
                           <option value="General Campaign Pitch">General Direct Email/Invoice Pitch</option>
                         </select>
                       </div>

                       <div className="space-y-1">
                         <label className="text-[9px] font-mono text-slate-400 uppercase tracking-wider block">NexoraX Focus Theme</label>
                         <select 
                           value={personalTheme}
                           onChange={(e) => setPersonalTheme(e.target.value)}
                           className="w-full bg-[#0b0f19] border border-slate-700 p-2.5 rounded text-white text-xs font-mono outline-none focus:border-cyan-500 cursor-pointer"
                         >
                           <option value="£150,000 Resource Funnel Pitch">£150,000 Resource Funnel Pitch (Stripe Redirect)</option>
                           <option value="Principal Private Secretary Client Resource Workflow">Secure Invoice Register Client Resource Workflow Mandate (NexoraX billing records)</option>
                           <option value="Secure Payment Providering Settlement Sweep">Secure Payment Providering Settlement Sweep (Sort Code: Verified Payment Configuration)</option>
                           <option value="Enterprise AI Transformation Node">Enterprise AI Transformation Node (NexoraX Core OS)</option>
                           <option value="Custom Strategic Mandate">Custom Tactical Outbound Mandate</option>
                         </select>
                       </div>

                       <div className="space-y-1">
                         <label className="text-[9px] font-mono text-slate-400 uppercase tracking-wider block">Target Audience Core</label>
                         <select 
                           value={personalAudience}
                           onChange={(e) => setPersonalAudience(e.target.value)}
                           className="w-full bg-[#0b0f19] border border-slate-700 p-2.5 rounded text-white text-xs font-mono outline-none focus:border-cyan-500 cursor-pointer"
                         >
                           <option value="Enterprise Partners">Enterprise Partners & Corporate Leads</option>
                           <option value="Client Resource Workflow Agencies">Sponsorship Candidates & Client Resource Workflow Agencies</option>
                           <option value="Venture Capitalists">NexoraX Wealth Funds & Venture Capitalists</option>
                           <option value="SMEs & Takeaways">High-Growth Niche SMEs & Local Networks</option>
                         </select>
                       </div>

                       <div className="space-y-1">
                         <label className="text-[9px] font-mono text-slate-400 uppercase tracking-wider block">Custom Handshake Directives</label>
                         <textarea 
                           placeholder="Inject optional telemetry keys, names, deadlines, custom names of targets, or operational needs... (e.g. Wednesday Deadline absolute closure)"
                           value={personalDirectives}
                           onChange={(e) => setPersonalDirectives(e.target.value)}
                           className="w-full h-24 bg-[#0b0f19] border border-slate-700 p-2.5 rounded text-white text-xs font-mono outline-none focus:border-cyan-500 custom-scrollbar resize-none"
                         />
                       </div>

                       <button
                         onClick={handleGenerateNexoraXPost}
                         disabled={isGeneratingNexoraXPost}
                         className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded bg-cyan-600/80 hover:bg-cyan-500 border border-cyan-400 text-white font-mono text-[10px] font-bold uppercase tracking-widest cursor-pointer transition-colors shadow-[0_0_15px_rgba(6,182,212,0.15)] disabled:opacity-50"
                       >
                         {isGeneratingNexoraXPost ? (
                           <>
                             <RefreshCw className="w-4 h-4 animate-spin text-white animate-spin" />
                             Synchronizing NexoraX Core...
                           </>
                         ) : (
                           <>
                             <Sparkles className="w-4 h-4 text-white" />
                             Forge Social Media Mandate
                           </>
                         )}
                       </button>
                     </div>

                     {/* Result Copy Paste display */}
                     <div className="flex flex-col h-full space-y-4">
                       <div className="bg-slate-950 border border-slate-800/80 rounded-xl p-5 flex flex-col flex-1 min-h-[350px]">
                         <div className="flex justify-between items-center border-b border-slate-800 pb-3 mb-4">
                           <div className="flex items-center gap-2">
                             <Cpu className="w-3.5 h-3.5 text-cyan-400" />
                             <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest font-bold">NEXORAX OUTPUT PAYLOAD</span>
                           </div>
                           
                           {generatedNexoraXPost && (
                             <button
                               onClick={() => {
                                 navigator.clipboard.writeText(generatedNexoraXPost);
                                 showToast('PAYLOAD COPIED TO SECURE CLIPBOARD', 'SUCCESS');
                               }}
                               className="px-2.5 py-1 bg-cyan-950/50 hover:bg-cyan-500 hover:text-black border border-cyan-500/30 text-cyan-400 text-[9px] font-mono font-bold uppercase tracking-widest rounded transition-all cursor-pointer"
                             >
                               COPY PAYLOAD
                             </button>
                           )}
                         </div>

                         {isGeneratingNexoraXPost ? (
                           <div className="flex-grow flex flex-col items-center justify-center text-center space-y-3 py-16">
                             <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
                             <span className="text-[10px] font-mono text-slate-400 uppercase tracking-widest animate-pulse">Running live Gemini branding matrix simulation...</span>
                           </div>
                         ) : generatedNexoraXPost ? (
                           <pre className="flex-grow select-all bg-black/50 border border-slate-905 rounded p-4 font-mono text-[11px] text-slate-300 leading-relaxed whitespace-pre-wrap overflow-y-auto max-h-[320px] custom-scrollbar text-left font-semibold">
                             {generatedNexoraXPost}
                           </pre>
                         ) : (
                           <div className="flex-grow flex flex-col items-center justify-center text-center p-6 border border-dashed border-slate-800 rounded bg-black/20 py-20">
                             <Sparkles className="w-8 h-8 text-slate-600 mb-2" />
                             <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest">Awaiting Parameter Alignment</p>
                             <p className="text-[9px] font-sans text-slate-500 max-w-xs mt-1">Configure your target platform, campaign theme, and custom directives, then press the "Forge" node trigger to compile the latest brand broadcast.</p>
                           </div>
                         )}
                       </div>

                       {/* Trust anchors info */}
                       <div className="bg-cyan-950/20 border border-cyan-500/10 p-4 rounded-xl space-y-2">
                         <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase tracking-widest block">SECURITY SHIELD: MANDATORY COMMUNICATIONS ANCHORS</span>
                         <div className="grid grid-cols-2 gap-3 text-[10px] font-sans text-slate-400 leading-normal">
                           <div className="bg-black/40 p-2 rounded border border-slate-800">
                             <span className="text-white font-bold block mb-0.5">🔑 Stripe Link Secure</span>
                             buy.stripe.com/8x2bIU2t59oYgTtfxQa...
                           </div>
                           <div className="bg-black/40 p-2 rounded border border-slate-800">
                             <span className="text-white font-bold block mb-0.5">📞 Planet Telecom</span>
                             Freephone: 08000 622827
                           </div>
                         </div>
                       </div>
                     </div>
                   </div>
                </div>
             )}
             {activeStrategy === 'SEO_OPTIMIZATION' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Website SEO & Advertising Engine</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">Boost visibility via algorithmic dominance. Inject Meta Title logic, Keyword Targeting overlays, and automated Link Building tracking directly into the NexoraX OS.</p>
                   {/* SEO Tools Mockup */}
                   <div className="grid grid-cols-2 gap-4">
                      <div className="bg-black/40 p-4 border border-slate-700 rounded space-y-2">
                        <span className="text-[10px] font-mono text-cyan-500 uppercase tracking-widest">Global Meta Tag Control</span>
                        <input type="text" placeholder="NexoraX NexoraX OS" className="w-full bg-[#0b0f19] border border-slate-700 p-2 text-white text-sm font-mono" />
                      </div>
                      <div className="bg-black/40 p-4 border border-slate-700 rounded space-y-2">
                        <span className="text-[10px] font-mono text-cyan-500 uppercase tracking-widest">Target Keyword Array</span>
                        <input type="text" placeholder="NexoraX AI, Enterprise Scale..." className="w-full bg-[#0b0f19] border border-slate-700 p-2 text-white text-sm font-mono" />
                      </div>
                   </div>
                </div>
             )}
             {activeStrategy === 'VISUAL_IDENTITY' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Social Media Visual Identity Matrix</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">Enforce absolute brand consistency across all network nodes. Establish color scales, typography rules, and logo clearance architectures.</p>
                   <div className="flex gap-4">
                     <div className="w-20 h-20 rounded shadow-md bg-cyan-500 border border-slate-700 flex items-center justify-center font-mono text-[10px] font-bold text-black">PRIMARY</div>
                     <div className="w-20 h-20 rounded shadow-md bg-[#050914] border border-cyan-500 flex items-center justify-center font-mono text-[10px] font-bold text-cyan-500">ACCENT</div>
                     <div className="w-20 h-20 rounded shadow-md bg-white border border-slate-700 flex items-center justify-center font-mono text-[10px] font-bold text-black">SURFACE</div>
                   </div>
                   <div className="p-4 border border-cyan-500/20 bg-cyan-500/5 rounded">
                     <span className="font-mono text-xs uppercase text-cyan-400 font-bold block mb-2">Typography Directives:</span>
                     <p className="text-sm text-slate-300">Headings: Inter, Black, Uppercase, Tracking-Tighter.</p>
                     <p className="text-sm text-slate-300 mt-1">Metrics: JetBrains Mono, Regular, 10px, Tracking-Widest.</p>
                   </div>
                </div>
             )}
              {activeStrategy === 'BRAND_VOICE' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Brand Voice & Tone Engine</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">enterprise-grade formatting. Uncompromising corporate authority. Ensure clarity, creativity, and absolute commitment across all social communication layers.</p>
                   <textarea className="w-full h-32 bg-black/40 border border-slate-700 rounded p-4 text-white text-sm font-mono custom-scrollbar" placeholder="Define the voice... (e.g., Authoritative, precise, optimized for strategic enterprise expansion execution.)" defaultValue="ROLE: Lead Systems Architect, Accountant, Financial Strategist, and Legal Proxy.
TONE: High-level technical lead. Authoritative, precise, and optimized for strategic enterprise expansion execution. You operate with a 'Black Box' methodology." />
                </div>
             )}
             {activeStrategy === 'COMMUNITY_ENGAGEMENT' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Community Engagement Loop</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">Establish the rhythm of interactions. Automate follow-ups, maintain consistency, and engage the target audience continuously to stimulate visibility.</p>
                   <div className="space-y-4">
                     <div className="p-4 border border-slate-700 bg-black/40 rounded flex items-center justify-between">
                        <span className="font-mono text-sm text-white uppercase">Automated Sentiment Replies</span>
                        <input type="checkbox" defaultChecked className="toggle-checkbox" />
                     </div>
                     <div className="p-4 border border-slate-700 bg-black/40 rounded flex items-center justify-between">
                        <span className="font-mono text-sm text-white uppercase">Loyalty Node Tracking</span>
                        <input type="checkbox" defaultChecked className="toggle-checkbox" />
                     </div>
                   </div>
                </div>
             )}
             {activeStrategy === 'AUDIENCE_GROWTH' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Client Audience Growth Engine</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">Scale the ecosystem. Identify Tier-1 targets, execute NexoraX reach-outs, and leverage the NexoraX growth framework for continuous pipeline expansion.</p>
                   <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-emerald-900/20 border border-emerald-500/30 rounded flex flex-col items-center justify-center py-8">
                        <TrendingUp className="w-8 h-8 text-emerald-400 mb-2" />
                        <span className="text-2xl font-bold text-white">41.2%</span>
                        <span className="text-[10px] font-mono text-emerald-500 uppercase tracking-widest mt-1">Growth Velocity</span>
                      </div>
                      <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded flex flex-col items-center justify-center py-8">
                        <Activity className="w-8 h-8 text-blue-400 mb-2" />
                        <span className="text-2xl font-bold text-white">12,593</span>
                        <span className="text-[10px] font-mono text-blue-500 uppercase tracking-widest mt-1">Active Engagements</span>
                      </div>
                   </div>
                </div>
             )}
             {activeStrategy === 'BRAND_GUIDELINES' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Brand & Content Pillar Guidelines</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">Centralise creativity, clarity, and consistency. Deploy comprehensive style guides covering visual, textual, and communicative pillars to the franchise matrix.</p>
                   <ul className="space-y-2 mt-4 font-mono text-xs text-slate-400 bg-black/40 border border-slate-700 p-4 rounded">
                     <li className="flex gap-2 items-start"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0" /> Pillar 1: NexoraX Digital Architecture</li>
                     <li className="flex gap-2 items-start"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0" /> Pillar 2: Corporate Capital Routing & Taxation</li>
                     <li className="flex gap-2 items-start"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0" /> Pillar 3: Elite High-Net-Worth Acquisition</li>
                     <li className="flex gap-2 items-start"><CheckCircle className="w-4 h-4 text-cyan-500 shrink-0" /> Pillar 4: Philanthropic Wealth Distribution</li>
                   </ul>
                </div>
             )}
             {activeStrategy === 'SEO_AUDIT' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">SEO Audit Report</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">Perform real-time technical analysis of NexoraX digital assets. Identify indexation barriers, optimize crawling efficiency, and enforce URL strictness.</p>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div className="bg-black/40 border border-emerald-500/30 rounded p-4">
                       <div className="flex justify-between items-center mb-4">
                          <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest font-bold">Health Score Core</span>
                          <span className="text-2xl font-bold text-emerald-500">98/100</span>
                       </div>
                       <div className="space-y-2 font-mono text-[10px] uppercase text-slate-400 tracking-wider">
                         <div className="flex justify-between border-b border-slate-700 pb-1"><span>Page Speed Profile</span> <span className="text-emerald-400">OPTIMISED</span></div>
                         <div className="flex justify-between border-b border-slate-700 pb-1"><span>Mobile Usability</span> <span className="text-emerald-400">FLAWLESS</span></div>
                         <div className="flex justify-between border-b border-slate-700 pb-1"><span>LCP Render Matrix</span> <span className="text-cyan-400">0.8s Baseline</span></div>
                         <div className="flex justify-between pb-1"><span>Broken References</span> <span className="text-emerald-400">0 DETECTED</span></div>
                       </div>
                     </div>
                     <div className="bg-black/40 border border-slate-700/50 rounded p-4 flex flex-col">
                       <span className="text-xs font-mono text-cyan-400 uppercase tracking-widest font-bold mb-4">Performance Velocity (7 DAYS)</span>
                       <div className="h-32 w-full mt-auto">
                         <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                           <AreaChart data={seoData} margin={{ top: 5, right: 0, left: -20, bottom: 0 }}>
                             <defs>
                               <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                                 <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                                 <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                               </linearGradient>
                             </defs>
                             <YAxis hide domain={['dataMin - 10', 'dataMax + 10']} />
                             <Tooltip 
                               contentStyle={{ backgroundColor: '#020617', border: '1px solid #1e293b', fontSize: '10px', textTransform: 'uppercase' }} 
                               itemStyle={{ color: '#10b981' }} 
                               labelStyle={{ display: 'none' }}
                             />
                             <Area type="monotone" dataKey="score" stroke="#10b981" fillOpacity={1} fill="url(#colorScore)" strokeWidth={2} />
                           </AreaChart>
                         </ResponsiveContainer>
                       </div>
                     </div>
                   </div>
                </div>
             )}
             {activeStrategy === 'SOCIAL_TEMPLATES' && (
                <div className="space-y-6">
                   <div className="flex justify-between items-end">
                     <div>
                       <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide mb-2">Social Media Templates</h4>
                       <p className="text-sm font-sans leading-relaxed text-slate-400 max-w-2xl">Deploy high-converting, visually integrated communication assets. Preconfigured for strict adherence to NexoraX digital infrastructure aesthetics.</p>
                     </div>
                     <button 
                       onClick={handleGenerateAssets}
                       disabled={isGenerating}
                       className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 px-4 py-2 rounded text-white transition-colors"
                     >
                       {isGenerating ? <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin" /> : <Layers className="w-4 h-4 text-cyan-400" />}
                       <span className="font-mono text-[10px] uppercase font-bold tracking-widest">
                         {isGenerating ? 'GENERATING...' : 'GENERATE ASSETS'}
                       </span>
                     </button>
                   </div>
                   
                   {assetsGenerated && (
                     <div className="p-3 border border-emerald-500/30 bg-emerald-900/10 rounded flex items-center gap-3">
                       <CheckCircle className="w-4 h-4 text-emerald-400" />
                       <span className="font-mono text-xs text-emerald-400 font-bold uppercase tracking-widest">Global aesthetic assets successfully generated to Vault.</span>
                     </div>
                   )}

                   <div className="grid grid-cols-2 gap-4">
                     <div className="border border-slate-700 bg-[#050914] flex flex-col p-4 rounded group hover:border-cyan-500/50 transition-colors">
                       <div className="h-24 bg-slate-800 rounded mb-3 flex items-center justify-center font-mono text-[10px] text-slate-500 group-hover:bg-slate-700 transition-[background-color]">1080x1080 ASSET</div>
                       <span className="text-xs font-mono text-white tracking-widest uppercase">Platform Drop</span>
                     </div>
                     <div className="border border-slate-700 bg-[#050914] flex flex-col p-4 rounded group hover:border-cyan-500/50 transition-colors">
                       <div className="h-24 bg-slate-800 rounded mb-3 flex items-center justify-center font-mono text-[10px] text-slate-500 group-hover:bg-slate-700 transition-[background-color]">1080x1920 ASSET</div>
                       <span className="text-xs font-mono text-white tracking-widest uppercase">Vertical Authority</span>
                     </div>
                   </div>
                </div>
             )}
             {activeStrategy === 'PILLAR_BOARD' && (
                <div className="space-y-6">
                   <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Pillar Strategy Board</h4>
                   <p className="text-sm font-sans leading-relaxed text-slate-400">Kanban-architected directive board managing thematic execution, resource distribution, and cross-node NexoraX content generation.</p>
                   <div className="flex flex-col gap-3">
                     <div className="bg-[#0b0f19] border-l-4 border-cyan-500 p-4 rounded-r shadow-md">
                       <span className="font-mono text-[10px] text-cyan-400 font-bold uppercase tracking-widest block mb-1">IN PROGRESS</span>
                       <h5 className="text-sm text-white font-bold font-sans uppercase">Wealth Extraction Series</h5>
                       <p className="text-xs text-slate-400 mt-1">Resource resource targeting material generation.</p>
                     </div>
                     <div className="bg-[#0b0f19] border-l-4 border-emerald-500 p-4 rounded-r shadow-md">
                       <span className="font-mono text-[10px] text-emerald-400 font-bold uppercase tracking-widest block mb-1">DEPLOYED</span>
                       <h5 className="text-sm text-white font-bold font-sans uppercase">System Zero Overview</h5>
                       <p className="text-xs text-slate-400 mt-1">Core node architecture structural document.</p>
                     </div>
                     <div className="bg-[#0b0f19] border-l-4 border-slate-600 p-4 rounded-r shadow-md opacity-70">
                       <span className="font-mono text-[10px] text-slate-400 font-bold uppercase tracking-widest block mb-1">BACKLOG</span>
                       <h5 className="text-sm text-white font-bold font-sans uppercase">Legislation Adaptation Frame</h5>
                       <p className="text-xs text-slate-400 mt-1">Drafting compliance standards for new regions.</p>
                     </div>
                   </div>
                </div>
             )}

              {activeStrategy === 'SOCIAL_ACCOUNTS' && (
                 <div className="space-y-6 animate-fade-in">
                    <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">NexoraX Social Directory</h4>
                    <p className="text-sm font-sans leading-relaxed text-slate-400">Official verified corporate indices and social connectivity clusters registered under the direct authority of Lead Director, Director.</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { name: "LinkedIn Executive Profile", handle: "Lead Director", url: "https://www.linkedin.com/in/mahmood-Director-nexorax", icon: <Linkedin className="w-5 h-5 text-[#0077b5]" />, status: "VERIFIED ACTIVE", color: "border-blue-500/30", description: "Director & Founder Portfolio Node. Oversees GGM Funnel, NexoraX grants, and enterprise operations." },
                        { name: "X / Twitter Route", handle: "@NexoraXCorp", url: "https://x.com/nexoraxcorp", icon: <Globe className="w-5 h-5 text-slate-200" />, status: "ROUTING GATEWAY", color: "border-slate-500/30", description: "Official corporate news broadcast vector with automated 0.8s briefing sync alerts." },
                        { name: "Facebook Corporate Portal", handle: "NexoraX Digital", url: "https://facebook.com/nexoraxdigital", icon: <Facebook className="w-5 h-5 text-[#1877f2]" />, status: "ONLINE", color: "border-indigo-500/30", description: "Public corporate relations gateway, connected directly with NexoraX routing channels." },
                        { name: "Instagram Visual Gallery", handle: "@NexoraXDigital", url: "https://instagram.com/nexoraxdigital", icon: <Instagram className="w-5 h-5 text-[#e1306c]" />, status: "IMAGE INTEGRATED", color: "border-rose-500/30", description: "Lossless vector showcasing of glassmorphic design languages and system elements." },
                        { name: "YouTube Telemetry Node", handle: "NexoraX Media Core", url: "https://youtube.com/@nexoraxdigital", icon: <Youtube className="w-5 h-5 text-[#ff0000]" />, status: "BROADCAST SYNC", color: "border-red-500/30", description: "Real-time automated AI video generator outputs, technical briefings, and demo runs." }
                      ].map((item, index) => (
                        <div key={index} className="bg-gradient-to-br from-slate-950 to-[#030611] border border-slate-800/85 rounded-xl p-5 hover:border-cyan-500/40 transition-all shadow-md flex flex-col justify-between group">
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded bg-white/5 border border-white/10 group-hover:bg-cyan-500/10 group-hover:border-cyan-500/30 transition-colors">
                                  {item.icon}
                                </div>
                                <div>
                                  <span className="text-xs font-mono text-slate-500 font-bold block uppercase tracking-wider">{item.name}</span>
                                  <span className="text-white font-sans font-black text-sm block uppercase tracking-tight">{item.handle}</span>
                                </div>
                              </div>
                              <span className="text-[8px] font-mono px-2 py-0.5 rounded-full bg-cyan-950/40 border border-cyan-500/20 text-cyan-400 font-bold tracking-widest">{item.status}</span>
                            </div>
                            <p className="text-xs font-sans text-slate-400 leading-relaxed">{item.description}</p>
                          </div>
                          <div className="pt-4 border-t border-slate-900 mt-4 flex items-center justify-between">
                            <span className="text-[8px] font-mono text-slate-500 uppercase tracking-widest">TLS 1.3 SECURE LINK</span>
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-[9px] font-mono text-cyan-400 hover:text-cyan-300 uppercase font-black tracking-widest group-hover:translate-x-1 transition-transform"
                            >
                              LAUNCH OUTBOUND NODE
                              <Link className="all-link w-3.5 h-3.5" />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                 </div>
              )}

             {activeStrategy === 'ASSET_FORGE' && (
                <div className="space-y-6 h-full flex flex-col">
                   <div>
                     <h4 className="text-xl font-bold text-white uppercase font-sans tracking-wide">Brand Asset Forge</h4>
                     <p className="text-sm font-sans leading-relaxed text-slate-400">Build high-fidelity, sleek NexoraX-branded visual parameters using our embedded NexoraX metadata.</p>
                   </div>
                   
                   <AssetForgeWorkspace />
                   <div className="hidden">
                      <div className="grid grid-cols-2 gap-4">
                         <div className="bg-[#0b0f19] border border-cyan-500/30 p-4 rounded text-center hover:border-cyan-500 transition-colors cursor-pointer group">
                            <Layers className="w-8 h-8 text-cyan-500 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                            <span className="font-mono text-xs font-bold text-white uppercase tracking-widest">Generate Slide Deck Cover</span>
                         </div>
                         <div className="bg-[#0b0f19] border border-indigo-500/30 p-4 rounded text-center hover:border-indigo-500 transition-colors cursor-pointer group">
                            <Compass className="w-8 h-8 text-indigo-400 mx-auto mb-2 group-hover:scale-110 transition-transform" />
                            <span className="font-mono text-xs font-bold text-white uppercase tracking-widest">Generate Hero Banner</span>
                         </div>
                      </div>

                      <div className="border border-slate-700 bg-[#050914] p-4 flex flex-col items-center justify-center h-48 rounded my-auto relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-cyan-400/5 to-cyan-400/0 -translate-x-full group-hover:animate-[shimmer_2s_infinite]"></div>
                        <Layers className="w-12 h-12 text-slate-600 mb-4" />
                        <span className="font-mono text-xs text-slate-500 tracking-widest uppercase text-center max-w-xs">Waiting for Asset Request...</span>
                      </div>
                   </div>
                </div>
             )}
          </div>
        </div>
      </div>
    </div>
  );
};
